import classNames from "classnames";
import { Variants } from "framer-motion";
import { ButtonHTMLAttributes, ElementType, ReactNode } from "react";
import useMeasure from "util/useMeasure";
import * as shadowMakers from "util/shadowMakers";
import ShapeFullScreenOverlay from "./ShapeFullScreenOverlay/ShapeFullScreenOverlay";
import { theming, shadowClassName, faceClassName } from "./theming";
import useOnClick from "./useOnClick";
import LoadingOverlayContent from "./ShapeFullScreenOverlay/LoadingOverlayContent";
import { BottomText } from "./BottomText";

interface SharedProps {
  shape: "circle" | "square";
  theme: keyof typeof theming | "transparent";
  fullScreenOverlay?: (props: { childrenVariants: Variants }) => ReactNode;
  active?: boolean;
  onLoadableClick?: () => Promise<string>; // Only onClick or onLoadableClick can be defined at a time
  onClick?: () => void; // Only onClick or onLoadableClick can be defined at a time
  outline?: "thin" | "thick";
  sizeClassName?: string;
  disabled?: boolean;
  buttonType?: ButtonHTMLAttributes<HTMLButtonElement>["type"];
  as?: ElementType;
  href?: string;
  routerType?: "replace" | "push";
}

interface IconProps extends SharedProps {
  type: "icon";
  icon: (props: { className: string }) => ReactNode;
  text?: string;
  textSize?: "normal" | "small";
  textOverflowBehavior?: "ellipsis" | "wrap";
}

interface ContentProps extends SharedProps {
  type: "content";
  children: ReactNode;
}

export type Props = ContentProps | IconProps;

export default function Shape(props: Props) {
  const {
    type,
    shape,
    fullScreenOverlay,
    theme,
    active,
    onLoadableClick,
    outline,
    sizeClassName,
    disabled,
    buttonType,
    href,
    as: Tag = href ? "a" : "button",
  } = props;

  const [measurements, ref] = useMeasure<HTMLButtonElement>({
    enabled: !!fullScreenOverlay || !!onLoadableClick,
  });
  const {
    onClick,
    loadingState,
    fullScreenOverlayOpen,
    setFullScreenOverlayOpen,
  } = useOnClick(props);

  const hasShadow = outline !== "thin" && theme !== "transparent";
  const overlayTheme =
    theming[theme === "transparent" ? transparentOverlayTheme : theme];

  return (
    <div className={classNames(sizeClassName || "w-full")}>
      <div className="aspect-h-1 aspect-w-1 relative">
        <Tag
          onTouchStart={() => {}}
          type={buttonType}
          disabled={disabled}
          href={href}
          title={type === "icon" ? props.text : undefined}
          ref={ref}
          onClick={onClick}
          className={classNames(
            "group focus:outline-none",
            "absolute inset-0",
            shape === "circle" && "rounded-full",
            sizeClassName,
            outline === "thin" && "mb-1",
            shadowMakers.parentTransform({
              active: active === true,
              disabled: false,
              hasShadow,
            })
          )}
        >
          {hasShadow && <div className={shadowClassName(props)} />}

          {fullScreenOverlay && (
            <ShapeFullScreenOverlay
              fullScreenOverlay={fullScreenOverlay}
              open={fullScreenOverlayOpen}
              onRequestClose={() => setFullScreenOverlayOpen(false)}
              measurements={measurements}
              theme={overlayTheme}
            />
          )}

          {onLoadableClick && (
            <ShapeFullScreenOverlay
              open={loadingState !== false}
              measurements={measurements}
              theme={overlayTheme}
              fullScreenOverlay={(props) => (
                <LoadingOverlayContent {...props} loading={loadingState} />
              )}
            />
          )}

          <div className={faceClassName(props)}>
            {type === "icon" ? (
              <>{props.icon({ className: "w-[100%]" })}</>
            ) : (
              <>{props.children}</>
            )}
          </div>
        </Tag>
      </div>

      <BottomText {...props} />
    </div>
  );
}

const transparentOverlayTheme: keyof typeof theming = "green"; // todo: make a purple loader
